import React from 'react';
import { datasetsForRoute, datasetsForTab } from './datasetLocations';
import { useImportJobs } from './ImportJobContext';

interface RingProps {
  /** 0-100, or null when the total is not yet known. */
  percent: number | null;
  size?: number;
  title: string;
  /**
   * The job stopped moving. Without this the ring spun indefinitely on work
   * that was not happening — a batch whose worker died looks identical to one
   * that is merely slow, and the animation actively implies the latter.
   */
  stalled?: boolean;
}

/**
 * A small progress ring with the percentage inside it.
 *
 * Drawn as SVG rather than a CSS conic-gradient so the number sits inside the
 * ring at any size, and so it can degrade to a spinning arc when the total row
 * count is not yet known — which is the case for the first seconds of every
 * import, before the worker has counted the file. Showing "0%" then would
 * imply no progress rather than no information.
 */
const Ring: React.FC<RingProps> = ({ percent, size = 18, title, stalled }) => {
  const stroke = 2.5;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const known = percent !== null && Number.isFinite(percent);
  const clamped = known ? Math.max(0, Math.min(100, percent as number)) : 0;
  // A stalled job never spins: the arc freezes where it got to, and an
  // unknown-total stall draws a full ring so there is something to see.
  const indeterminate = !known && !stalled;

  return (
    <span
      className={
        'import-ring' +
        (indeterminate ? ' is-indeterminate' : '') +
        (stalled ? ' is-stalled' : '')
      }
      role="img"
      aria-label={title}
      title={title}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          className="import-ring-track"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
        />
        <circle
          className="import-ring-fill"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={
            known
              ? circumference * (1 - clamped / 100)
              : stalled
                ? 0
                : circumference * 0.75
          }
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      {/* "!" rather than a number when stalled: a frozen percentage reads as
          progress that simply has not moved yet. */}
      {stalled ? (
        <span className="import-ring-label">!</span>
      ) : (
        known && <span className="import-ring-label">{Math.round(clamped)}</span>
      )}
    </span>
  );
};

interface JobLike {
  dataset: string;
  dataset_label: string;
  processed: number;
  row_count: number;
  stalled?: boolean;
}

const summarise = (
  jobs: JobLike[]
): { percent: number | null; title: string; stalled: boolean } => {
  // Weighted across jobs by row count, so two concurrent imports of very
  // different sizes do not average into a misleading figure.
  const total = jobs.reduce((sum, j) => sum + (j.row_count || 0), 0);
  const done = jobs.reduce((sum, j) => sum + (j.processed || 0), 0);
  const percent = total > 0 ? (done / total) * 100 : null;
  const names = Array.from(new Set(jobs.map((j) => j.dataset_label))).join(', ');
  const stalled = jobs.some((j) => j.stalled);
  if (stalled) {
    return {
      percent,
      stalled: true,
      title: `${names} import has stopped responding — open the importer to retry`,
    };
  }
  return {
    percent,
    stalled: false,
    title:
      percent === null
        ? `Importing ${names} — starting up`
        : `Importing ${names} — ${Math.round(percent)}% done`,
  };
};

/** Ring for a navbar route, when something is importing into that page. */
export const RouteImportRing: React.FC<{ path: string }> = ({ path }) => {
  const { activeImports } = useImportJobs();
  const keys = datasetsForRoute(path);
  if (keys.length === 0) return null;
  const jobs = activeImports.filter((job) => keys.includes(job.dataset));
  if (jobs.length === 0) return null;
  const { percent, title, stalled } = summarise(jobs);
  return <Ring percent={percent} title={title} stalled={stalled} />;
};

/** Ring for a tab within a page, e.g. Facilities then Utilities. */
export const TabImportRing: React.FC<{ tabKey: string }> = ({ tabKey }) => {
  const { activeImports } = useImportJobs();
  const keys = datasetsForTab(tabKey);
  if (keys.length === 0) return null;
  const jobs = activeImports.filter((job) => keys.includes(job.dataset));
  if (jobs.length === 0) return null;
  const { percent, title, stalled } = summarise(jobs);
  return <Ring percent={percent} title={title} stalled={stalled} size={16} />;
};

export default Ring;
